import {
  ActionIcon,
  Box,
  Button,
  Card,
  Center,
  Flex,
  Grid,
  Group,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  Typography
} from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";
import {
  IconCheckupList,
  IconClipboardText,
  IconEdit,
  IconMicrophone,
  IconPrinter,
  IconRefresh,
  IconSparkles
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { Markup } from "interweave";
import { useState } from "react";
import { useMsal } from "@azure/msal-react";
import type { ProspectInDetails } from "@/models/ProspectInDetails";
import {
  getDocumentsListForProspectQuery,
  getProspectByIdQuery,
  getRecordingsListForProspectQuery,
  useApi
} from "@/api";
import { generateLivingStoryMutation } from "@/api/prospects/generateLivingStory";
import { updateLivingStoryMutation } from "@/api/prospects/updateLivingStory";
import { ReviewEditContentModal } from "@/components/ReviewEditContentModal";
import { canEditLivingStory, canGenerateLivingStory } from "@/security/logic";

export const Route = createFileRoute("/prospects/$prospectId/living-story")({
  component: RouteComponent,
  loader: () => {
    return {
      crumb: "Living Story"
    };
  }
});

function RouteComponent() {
  const { prospectId } = Route.useParams();
  const { userClaim, prospectName } = Route.useRouteContext();
  const { secureAxios } = useApi();
  const queryClient = useQueryClient();
  const { accounts } = useMsal();
  const currentUserName = accounts[0]?.name;

  const { data: prospect } = useQuery<ProspectInDetails | undefined>(
    getProspectByIdQuery(secureAxios, Number(prospectId))
  );

  const { data: recordings = [] } = useQuery(
    getRecordingsListForProspectQuery(secureAxios, Number(prospectId))
  );

  const { data: documents = [] } = useQuery(
    getDocumentsListForProspectQuery(secureAxios, Number(prospectId))
  );

  const hasGeneratePermission = canGenerateLivingStory(
    userClaim,
    prospect?.communityId ?? 0
  );
  const hasEditPermission = canEditLivingStory(
    userClaim,
    prospect?.communityId ?? 0
  );

  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  const generateMutation = useMutation<string, Error, void>({
    ...generateLivingStoryMutation(secureAxios, Number(prospectId)),
    onSuccess: (story: string) => {
      queryClient.setQueryData<ProspectInDetails | undefined>(
        ["prospect", Number(prospectId)],
        (old) =>
          old
            ? {
                ...old,
                livingStoryHtml: story,
                lifeStoryGenerated: true,
                hasLivingStoryInputsChanged: false,
                livingStoryLastGeneratedBy: currentUserName
                  ? { id: 0, displayName: currentUserName }
                  : undefined,
                livingStoryLastGeneratedAt: new Date().toISOString()
              }
            : old
      );
    }
  });

  const updateMutation = useMutation<string, Error, string>({
    ...updateLivingStoryMutation(secureAxios, Number(prospectId)),
    onSuccess: (updatedStory: string) => {
      queryClient.setQueryData<ProspectInDetails | undefined>(
        ["prospect", Number(prospectId)],
        (old) =>
          old
            ? {
                ...old,
                livingStoryHtml: updatedStory,
                livingStoryLastGeneratedBy: currentUserName
                  ? { id: 0, displayName: currentUserName }
                  : undefined,
                livingStoryLastGeneratedAt: new Date().toISOString()
              }
            : old
      );
      setIsReviewModalOpen(false);
    }
  });

  const includedRecordings = recordings.filter(
    (r) => r.includeSummaryInLivingStory || r.includeNotesInLivingStory
  );
  const includedDocuments = documents.filter(
    (d) => d.includeDetailsInLivingStory
  );
  const hasInputs =
    includedRecordings.length > 0 || includedDocuments.length > 0;

  // Check if inputs have changed since last generation (computed by backend from timestamps)
  const hasInputsChanged = generateMutation.isSuccess
    ? false
    : (prospect?.hasLivingStoryInputsChanged ?? true);

  const isReadinessChecklistComplete =
    prospect?.docInterviewComplete &&
    prospect.teaAndChatComplete &&
    prospect.finalFollowUpComplete &&
    prospect.decisionMade &&
    prospect.movedIn;

  const displayedStory: string | undefined =
    generateMutation.data ?? prospect?.livingStoryHtml;
  const isStoryAvailable = !!displayedStory;

  const handlePrint = () => {
    if (!displayedStory) {
      return;
    }

    const printWindow = window.open("", "_blank", "width=800,height=600");
    if (printWindow) {
      const doc = printWindow.document;
      doc.title = `${prospectName} - Living Story`;

      const style = doc.createElement("style");
      style.textContent = `
        body {
          font-family: system-ui, -apple-system, sans-serif;
          line-height: 1.6;
          padding: 2rem;
          color: #1a1a1a;
          max-width: 800px;
          margin: 0 auto;
        }
        h1 {
          font-size: 1.5rem;
          margin-bottom: 1.5rem;
          border-bottom: 1px solid #e9ecef;
          padding-bottom: 0.5rem;
        }
        .content {
          white-space: pre-wrap;
          font-size: 1rem;
        }
        @media print {
          body { padding: 0; margin: 1.5cm; }
        }
      `;

      doc.head.appendChild(style);
      // Doing this because printWindow.document.write() is deprecated beacause of its idiosyncratic behavior
      doc.body.innerHTML = `
        <h1>${prospectName} - Living Story</h1>
        <div class="content">${displayedStory}</div>
      `;

      printWindow.focus();
      printWindow.print();
    }
  };

  const reasonCantGenerate = !hasGeneratePermission
    ? `You do not have access to ${isStoryAvailable ? "update" : "generate"} the Living Story.`
    : !isReadinessChecklistComplete
      ? "Complete the Neighborhood Readiness Checklist to generate"
      : isStoryAvailable && !hasInputsChanged
        ? "Add, remove or change inputs to update the Living Story"
        : null;

  return (
    <>
      <Grid gutter="md" mt="md">
        <Grid.Col span={{ base: 12, lg: 7 }} order={{ base: 2, lg: 1 }}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Title order={4} mt="0">
                {prospectName} - Living Story
              </Title>
              <ActionIcon
                variant="subtle"
                color="gray"
                disabled={!isStoryAvailable}
                onClick={handlePrint}
              >
                <IconPrinter size={23} />
              </ActionIcon>
            </Group>

            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              align={{ base: "flex-start", lg: "center" }}
              gap="xs"
              mb="md"
            >
              <Group gap="xs">
                <ThemeIcon color="blue" variant="light" size="sm">
                  <IconSparkles size={16} />
                </ThemeIcon>
                <Text c="blue" size="sm" fw={500}>
                  AI Generated - Review for Accuracy
                </Text>
              </Group>
              {prospect?.livingStoryLastGeneratedBy &&
                prospect.livingStoryLastGeneratedAt && (
                  <Group>
                    <Text c="blue" size="xs">
                      Generated by:{" "}
                      <Text component="span">
                        {prospect.livingStoryLastGeneratedBy.displayName}
                      </Text>
                    </Text>
                    <Text c="blue" size="xs">
                      On:{" "}
                      <Text component="span">
                        {DateTime.fromISO(prospect.livingStoryLastGeneratedAt, {
                          zone: "utc"
                        })
                          .setZone("America/Denver")
                          .toFormat("yyyy-MM-dd h:mm a 'MT'")}
                      </Text>
                    </Text>
                  </Group>
                )}
            </Flex>

            {isStoryAvailable ? (
              <Typography>
                <Markup className="markup-content" content={displayedStory} />
              </Typography>
            ) : (
              <Center h={200}>
                <Stack align="flex-start" gap="xs" ta="left" fw={500}>
                  <Text>This resident's story hasn't been created yet.</Text>
                  <Text>
                    You must complete the Neighborhood Readiness Checklist and
                    upload recordings, documents or notes before you may
                    generate the Living Story.
                  </Text>
                  <Text>
                    The Living Story helps capture meaningful life details,
                    preferences, and history - so that care teams can provide
                    more personalized support.
                  </Text>
                </Stack>
              </Center>
            )}
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 5 }} order={{ base: 1, lg: 2 }}>
          <Card
            shadow="sm"
            padding="lg"
            radius="md"
            withBorder
            bg="navy.0"
            c="navy.9"
            style={{ borderColor: "var(--mantine-color-navy-2)" }}
          >
            <Title order={4} mt="0" mb="md">
              Living Story Inputs
            </Title>

            <Flex
              direction={{ base: "column", md: "row", lg: "column" }}
              gap="md"
              align={{ base: "center", md: "flex-start", lg: "center" }}
            >
              <Box style={{ flex: 1, minWidth: 0, width: "100%" }}>
                {hasInputs ? (
                  <Table
                    styles={{
                      th: {
                        color: "var(--mantine-color-optimaBlue-9)",
                        borderBottom: "1px solid var(--mantine-color-black)"
                      },
                      td: {
                        fontSize: "var(--mantine-font-size-sm)",
                        color: "var(--mantine-color-navy-9)",
                        borderBottom: "1px solid var(--mantine-color-navy-4)"
                      }
                    }}
                  >
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ width: "55%" }}>Title</Table.Th>
                        <Table.Th style={{ width: "15%" }}>
                          Date Created
                        </Table.Th>
                        <Table.Th style={{ width: "30%" }}>Input Type</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {includedRecordings.map((recording) => (
                        <Table.Tr key={recording.id}>
                          <Table.Td>
                            <Text size="sm" fw={700} c="navy.9">
                              {recording.recordingTitle || "Untitled"}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            {DateTime.fromISO(recording.startedDate).toFormat(
                              "yyyy-MM-dd"
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Stack gap={0}>
                              {recording.includeSummaryInLivingStory && (
                                <Group gap={5}>
                                  <IconMicrophone size={16} />
                                  <Text size="sm">Summary</Text>
                                </Group>
                              )}
                              {recording.includeNotesInLivingStory && (
                                <Group gap={5}>
                                  <IconMicrophone size={16} />
                                  <Text size="sm">Note</Text>
                                </Group>
                              )}
                            </Stack>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                      {includedDocuments.map((document) => (
                        <Table.Tr key={`doc-${document.id}`}>
                          <Table.Td>
                            <Text size="sm" fw={700} c="navy.9">
                              {document.title || "Untitled"}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <span className="whitespace-nowrap">
                              {DateTime.fromISO(document.createdAt).toFormat(
                                "yyyy-MM-dd"
                              )}
                            </span>
                          </Table.Td>
                          <Table.Td>
                            <Group gap={5}>
                              <IconClipboardText size={16} />
                              <Text size="sm">Doc Note</Text>
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                ) : (
                  <Text c="dimmed" ta="center" py="xl">
                    No inputs exist for this prospect's living story
                  </Text>
                )}
              </Box>

              <Stack gap="xs">
                <Button
                  fullWidth
                  disabled={!isStoryAvailable || !hasEditPermission}
                  onClick={() => setIsReviewModalOpen(true)}
                  leftSection={<IconEdit />}
                >
                  Review and Edit Living Story
                </Button>
                {!hasEditPermission && (
                  <Text c="dimmed" size="xs" ta="center">
                    You do not have access to edit the Living Story.
                  </Text>
                )}
                <Button
                  fullWidth
                  variant="outline"
                  disabled={!hasInputs || reasonCantGenerate !== null}
                  loading={generateMutation.isPending}
                  onClick={() => generateMutation.mutate()}
                  leftSection={
                    isStoryAvailable ? <IconRefresh /> : <IconCheckupList />
                  }
                >
                  {isStoryAvailable ? "Update" : "Generate"} Living Story
                </Button>
                {reasonCantGenerate && (
                  <Text c="dimmed" size="xs" ta="center">
                    {reasonCantGenerate}
                  </Text>
                )}
              </Stack>
            </Flex>
          </Card>
        </Grid.Col>
      </Grid>

      {isReviewModalOpen && displayedStory && (
        <ReviewEditContentModal
          title={`${prospectName} - Living Story`}
          description="Review and edit the Living Story."
          confirmTitle="Update Living Story"
          confirmMessage="Are you sure you would like to update the Living Story?"
          contentHtml={displayedStory}
          isSubmitting={updateMutation.isPending}
          onApprove={(editedHtml) => updateMutation.mutate(editedHtml)}
          onClose={() => setIsReviewModalOpen(false)}
        />
      )}
    </>
  );
}
